import React from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import DatePicker from "../components/DatePicker";
import Select from "../components/Select";
import Button from "../components/Button";
import DataTableView from "../components/DataTableView2";
import useBatches from "../hooks/logics/batches/use-batches";
import { IGetAllBatchesDocs } from "../utils/types";
import ConfirmationDialog from "../components/ConfirmationDialog";
import MergeBatchModal from "../components/batches/MergeBatchModal";
import { batchStatusOptions, batchStatusMap } from "../utils/helpers";
const Batches = () => {
    const [batchToMerge, setBatchToMerge] = React.useState<IGetAllBatchesDocs | null>(null);
    const {
        form,
        batches,
        totalPage,
        loading,
        status,
        setStatus,
        fromDate,
        toDate,
        handleFromDate,
        handleToDate,
        filterBatches,
        handlePageClick,
        resetFilters,
        isApplyButtonDisabled,
        isClearButtonDisabled,
        searchParams,
        isClosingBatch,
        setSelectedBatchId,
        selectedBatchId,
        handleServiceCancel,
        refetch,
    } = useBatches();

    const columns = [
        {
            header: "Batch Number",
            className: "md:p-0 md:pl-10",
            accessor: (batch: IGetAllBatchesDocs) => (
                <div className="md:!pl-8">
                    <p className="text-md font-semibold text-black">{batch.batchNumber || "N/A"}</p>
                </div>
            ),
        },
        {
            header: "Orders Counts",
            accessor: (batch: IGetAllBatchesDocs) => batch.totalOrders ?? 0,
        },
        {
            header: "Driver",
            accessor: (batch: IGetAllBatchesDocs) => (
                batch.driver && batch.driver.length > 0 ? (
                    <Link to={`/dashboard/drivers/${batch.driver[0]._id}`}>
                        <p className="text-md font-normal text-black hover:text-primary transition break-all max-w-[280px] truncate">
                            {batch.driver[0].fullName}
                        </p>
                    </Link>
                ) : (
                    <p className="text-md text-gray-500 font-normal">Unassigned</p>
                )
            ),
        },
        {
            header: "Pickups",
            accessor: (batch: IGetAllBatchesDocs) => batch.totalPickUpStops ?? 0,
        },
        {
            header: "Dropoffs",
            accessor: (batch: IGetAllBatchesDocs) => batch.totalDropOffStops ?? 0,
        },
        {
            header: "Created",
            accessor: (batch: IGetAllBatchesDocs) =>
                batch.createdAt ? new Date(batch.createdAt).toLocaleDateString() : "N/A",
        },
        {
            header: "Status",
            accessor: (batch: IGetAllBatchesDocs) => (
                <div
                    className={twMerge(
                        "text-md font-semibold whitespace-nowrap capitalize",
                        batch.status === "completed" || batch.status === "rated_by_user" ? "text-green-600" : "",
                        batch.status === "cancelled" && "text-red-600",
                        batch.status === "merged" && "text-gray-500",
                        batch.status === "in_progress" && "text-blue-600",
                        batch.status === "forming" && "text-purple-600",
                        batch.status === "dispatched" && "text-orange-600",
                        batch.status === "pending_payment" && "text-yellow-600"
                    )}
                >

                    {batchStatusMap(batch.status, '')}

                </div>
            ),
        },
        {
            header: "Actions",
            accessor: (batch: IGetAllBatchesDocs) => (
                <div className="flex gap-4 items-center">
                    <Link to={`/dashboard/batches/${batch._id}`}>
                        <button className="text-primary cursor-pointer hover:underline">Details</button>
                    </Link>
                    {batch.status === "forming" && (
                        <>
                            <button
                                onClick={() => setBatchToMerge(batch)}
                                className="text-primary hover:underline cursor-pointer font-medium"
                            >
                                Merge
                            </button>
                            <button
                                onClick={() => setSelectedBatchId(batch._id)}
                                className="text-red-600 hover:text-red-800 cursor-pointer hover:underline"
                            >
                                Close Batch
                            </button>
                        </>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="w-full h-full">
                <div className="flex flex-col justify-center font-inter bg-gray-100">
                    <div className="w-full flex flex-wrap gap-4 justify-end items-center mb-4">
                        <form
                            ref={form}
                            onSubmit={filterBatches}
                            className="flex flex-wrap gap-x-2 justify-end items-center"
                        >
                            <div className="w-full flex flex-wrap items-center justify-end gap-4">
                                <div className="w-full flex flex-row gap-x-2 md:flex-row md:gap-x-4 md:w-max mt-4 md:mt-0">
                                    <div className="flex flex-col items-start md:items-center md:flex-row gap-y-1 w-1/2 md:w-full">
                                        <label className="mr-2">From:</label>
                                        <DatePicker
                                            placeholderText="MM/DD/YYYY"
                                            onChange={handleFromDate}
                                            name="fromDate"
                                            selected={fromDate ? new Date(fromDate) : null}
                                            className="min-h-0 h-8 md:h-10 py-1"
                                            inputClassName="py-0 md:py-2 h-8 md:h-10"
                                            popperClassName="!left-7"
                                        />
                                    </div>
                                    <div className="flex flex-col items-start md:items-center md:flex-row gap-y-1 w-1/2 md:w-full">
                                        <label className="mr-2">To:</label>
                                        <DatePicker
                                            placeholderText="MM/DD/YYYY"
                                            name="toDate"
                                            selected={toDate ? new Date(toDate) : null}
                                            onChange={handleToDate}
                                            className="min-h-0 h-8 md:h-10 py-1"
                                            inputClassName="py-0 md:py-2 h-8 md:h-10"
                                            popperClassName="!-left-7"
                                        />
                                    </div>
                                </div>
                                <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4">
                                    <Select
                                        name="status"
                                        onChange={(e) => setStatus(e.target.value)}
                                        value={status}
                                        className="md:w-40 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
                                        options={batchStatusOptions}
                                        placeholder="Status"
                                        containerClassName="w-1/2 md:w-auto"
                                    />
                                </div>
                                <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4 mb-8 md:mb-0">
                                    <Button
                                        disabled={isApplyButtonDisabled()}
                                        type="submit"
                                        className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10"
                                    >
                                        Apply
                                    </Button>
                                    <Button
                                        disabled={isClearButtonDisabled()}
                                        type="button"
                                        onClick={resetFilters}
                                        className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <DataTableView
                        data={batches}
                        columns={columns}
                        isFetching={loading}
                        emptyStateMessage="No batches found"
                        currentPage={parseInt(searchParams.get("page") || "1")}
                        setCurrentPage={(page: number) => handlePageClick({ selected: page - 1 })}
                        totalPages={totalPage}
                        height="min-h-[16rem]"
                    />
                </div>
            </div>
            <ConfirmationDialog
                isSubmitButtonDisabled={isClosingBatch}
                isSubmitting={isClosingBatch}
                handleClick={handleServiceCancel}
                isVisible={selectedBatchId !== ''}
                onHide={() => setSelectedBatchId('')}
                text='Are you sure you want to close this batch?'
            />
            <MergeBatchModal
                isVisible={Boolean(batchToMerge)}
                onHide={() => setBatchToMerge(null)}
                sourceBatch={batchToMerge}
                onSuccess={() => {
                    refetch();
                }}
            />
        </>
    );
};

export default Batches;